import React from 'react'

import { SimpleTable, TableSkeleton, type AddressCell, Alert } from '@/components/ui'
import { useApi, useDashboard, useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { dataSourceParam } from '@/insights/common/dataSourceParam'
import { validateDateRangeWithin30Days } from '@/insights/common/formatUtils'
import genericLocationDrilldown from '@/insights/common/genericLocationDrilldown'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { NonAlarmDateOptionsWithCustom } from '@/insights/components/CustomSingleSelect'
import {
  ApiService,
  type Asset,
  AssetTypeFilter,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { celsiusToUser } from '@/utils/conversions'
import { downloadCsv } from '@/utils/csvExport'
import { formatDate, standardDateTimeFormat } from '@/utils/formatDate'

interface ReeferHistoryParams extends InsightParametersType {
  companyId?: number
  assetId?: string
  date?: string
  from?: string
  to?: string
}

export interface ReeferHistory {
  id: number
  /** 'sample' rows carry state/temperatures, 'alarm' rows carry the alarm columns. */
  rowType: 'sample' | 'alarm'
  companyId: number
  assetId: number
  assetName: string
  sampleTime: Date
  geofence: string | null
  location: string | null
  latitude: number | null
  longitude: number | null
  reeferAlarm: string | null
  reeferEvent: string | null
  doorState: string | null
  reeferState: string | null
  ambientTemperature: number | null
  zone1Mode: string | null
  zone1SP: number | null
  zone1SA: number | null
  zone1RA: number | null
}

const ReeferHistory: InsightDefinition<ReeferHistoryParams, ReeferHistory[]> = {
  active: true,
  slug: 'asset/reefer-history',
  category: 'asset',
  name: 'Reefer History',
  description: 'List reefer history including alarms, events, door state and zone 1 temperatures',
  defaultWidth: 'full',
  defaultAspectRatio: 16 / 9,

  configuration: {
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      { id: 'assetId', control: 'AssetDropdownSingle', required: true, assetType: AssetTypeFilter.InTransit },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: NonAlarmDateOptionsWithCustom,
      },
      {
        id: 'customDate',
        label: 'Date',
        control: 'DateTimeRange',
        dependsOn: 'date',
        dependsOnValue: 'custom',
      },
    ],

    isValid: (configuration: ReeferHistoryParams) => {
      if (!configuration?.companyId || !configuration.assetId) {
        return 'Company and Asset are required.'
      }

      if (configuration.date === 'custom') {
        if (!configuration.from || !configuration.to) {
          return 'Enter the custom date range'
        }
        const fromdate = new Date(configuration.from)
        const todate = new Date(configuration.to)
        if (fromdate > todate) {
          return 'Start date must be before end date'
        }
        const dateValidation30Days = validateDateRangeWithin30Days(fromdate.toISOString(), todate.toISOString())
        if (dateValidation30Days !== true) {
          return dateValidation30Days
        }
      }

      return true
    },
  },

  load: async (api: ApiService, parameters: ReeferHistoryParams) => {
    const path = `/insights/asset/reefer-history?${buildQuery(parameters)}&skip=0&take=1000`
    const response = await api.get<ReeferHistory[]>({
      path,
      origin: 'ReeferHistory',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data?.length) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  csv: async (api: ApiService, parameters: ReeferHistoryParams, title: string) => {
    const path = '/insights/asset/reefer-history'
    await downloadCsv(api, path, parameters, title)
  },

  Display: ({ status, data, configuration }) => {
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    const units = useDashboard().configuration.units as 'Imperial' | 'Metric'
    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (showSkeleton) {
      return <TableSkeleton columns={12} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      // Wait until we're ready to display before displaying.
      // Framework will handle the other states like error and no data.
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      // Probably would never get here, but keeps typescript happy.
      return null
    }

    const parameters = configuration.parameters as ReeferHistoryParams
    const headers = [
      { key: 'Date', title: 'Date', size: 200 },
      'Geofence',
      'Last Known Address',
      'Reefer Alarm',
      'Reefer Event',
      'Door State',
      'Reefer State',
      'Ambient',
      'Zone 1 Mode',
      'Zone 1 Setpoint',
      'Zone 1 Supply Air',
      'Zone 1 Return Air',
    ]
    // Leave any empty cell blank. Sample rows leave the alarm columns empty and
    // alarm rows leave the state/temperature columns empty, so these blanks are
    // "not applicable to this row", not missing data.
    const blank = (value: string | null | undefined): string => value ?? ''
    const body = data
      .slice()
      .sort((a, b) => new Date(a.sampleTime).getTime() - new Date(b.sampleTime).getTime())
      .map((sample): Record<string, string | number | AddressCell> => {
        const sampleTime = new Date(sample.sampleTime)
        return {
          Date: formatDate(sampleTime, standardDateTimeFormat),
          Geofence: blank(sample.geofence),
          'Last Known Address': genericLocationDrilldown({
            configuration,
            address: sample.location ?? undefined,
            latitude: sample.latitude ?? undefined,
            longitude: sample.longitude ?? undefined,
            companyId: parameters.companyId,
            markerFields: {
              Asset: sample.assetName,
              Time: formatDate(sampleTime, standardDateTimeFormat),
            },
          }),
          'Reefer Alarm': blank(sample.reeferAlarm),
          'Reefer Event': blank(sample.reeferEvent),
          'Door State': blank(sample.doorState),
          'Reefer State': blank(sample.reeferState),
          Ambient: blank(celsiusToUser(sample.ambientTemperature, 1, units)),
          'Zone 1 Mode': blank(sample.zone1Mode),
          'Zone 1 Setpoint': blank(celsiusToUser(sample.zone1SP, 1, units)),
          'Zone 1 Supply Air': blank(celsiusToUser(sample.zone1SA, 1, units)),
          'Zone 1 Return Air': blank(celsiusToUser(sample.zone1RA, 1, units)),
        }
      })

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} cellClassNames={{ Date: 'text-nowrap' }} />
        </div>
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const [asset, setAsset] = React.useState<Asset>()
    const { companies } = useCompany()
    const { api } = useApi()
    const params = configuration.parameters as ReeferHistoryParams
    const units = useDashboard().configuration.units as 'Imperial' | 'Metric'
    React.useEffect(() => {
      if (!api) {
        return
      }
      const fn = async () => {
        if (params.assetId) {
          const response = await api.get<Asset[]>({
            path: `/assets?companyId=${params.companyId}&id=${params.assetId}&deleted=false${dataSourceParam(params)}`,
            origin: 'ReeferHistory#caption',
          })
          if (response.data?.length) {
            setAsset(response.data[0])
          } else {
            setAsset({ id: Number(params.assetId) } as Asset)
          }
        }
      }
      fn()
    }, [params])

    if (status.code === InsightStatusCode.ERROR) {
      // Framework will handle this
      return ''
    }
    const fields: Record<string, string> = {}
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (asset) {
      fields['Asset'] = asset.name
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    fields['Temperature Units'] = units === 'Imperial' ? '°F' : '°C'
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || (status.code === InsightStatusCode.ERROR && data === undefined)) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default ReeferHistory
